/**
 * DienstenPage — Alle IMPERIA AI diensten in detail.
 *
 * Gegroepeerd in 4 categorieën met expliciete uitleg van wat AI doet,
 * wat klant doet, en in welke tier het zit (Free / €14,95 / Managed AI).
 *
 * SEO: Eén H1 met "AI Marketing Diensten", H2 per category, H3 per dienst.
 * JSON-LD Service per dienst + BreadcrumbList.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import {
  Search, Target, Globe, MapPin, Link2, Wrench, Crown, Sparkles,
  BarChart3, TrendingUp, FileText, ShieldCheck, ArrowRight,
  CheckCircle2, Bot, UserCheck, Headphones, ChevronRight, Zap,
} from 'lucide-react';

/* ─── 12 diensten gegroepeerd in 4 categorieën ─── */
const CATEGORIES = [
  {
    id: 'vindbaarheid',
    title: 'Vindbaarheid in Google',
    intro: 'Hoger ranken op de zoekwoorden waar uw klanten op zoeken — automatisch.',
    services: [
      {
        id: 'seo',
        slug: 'ai-seo',
        icon: Search,
        title: 'AI SEO',
        seoIntent: 'beter vindbaar in Google',
        intro: 'Zoekmachine-optimalisatie zonder maanden wachten. Onze AI analyseert uw site, vergelijkt met concurrenten en voert direct de fixes door waar mogelijk.',
        wat: [
          'Technische SEO-audit (titles, meta, schema, sitemap)',
          'Content-audit per pagina (H1, woord-aantal, keyword density)',
          'Interne linkstructuur optimalisatie',
          'Core Web Vitals + page speed scoring',
          'AI-suggesties voor élke gevonden issue',
        ],
        uitvoering: 'Auto-push via REST API voor WordPress, Shopify, Webflow. Voor Wix/Squarespace: kant-en-klare snippets die u in 30 seconden plakt. Voor Joomla/Drupal: ons ops-team plaatst handmatig.',
        tiers: ['Free Scan: top-10 issues', 'Self-Service €14,95: alle issues + suggesties', 'Managed AI: AI voert fixes uit'],
      },
      {
        id: 'gbp',
        slug: 'google-bedrijfsprofiel',
        icon: MapPin,
        title: 'Google Bedrijfsprofiel',
        seoIntent: 'lokale vindbaarheid verbeteren',
        intro: 'Voor lokale dienstverleners is Google Maps de eerste klikbron. Een volledig geoptimaliseerd profiel verdubbelt uw lokale ranking.',
        wat: [
          'Categorieën + secundaire categorieën',
          'Uitgebreide beschrijving met lokale zoekwoorden',
          'Wekelijkse Local Posts (events, aanbiedingen, nieuws)',
          'Foto-management + Q&A monitoring',
          'Review-respons door AI (binnen 24u)',
        ],
        uitvoering: 'Direct via Google Business Profile API. AI publiceert posts, beantwoordt reviews en signaleert zwaktes.',
        tiers: ['Free Scan: GBP-audit', 'Self-Service €14,95: optimalisatie-tips', 'Managed AI: volledig beheer + posts'],
      },
      {
        id: 'lokaal',
        slug: 'lokale-vindbaarheid',
        icon: ShieldCheck,
        title: 'Lokale Online Zichtbaarheid',
        seoIntent: 'lokale klanten aantrekken',
        intro: 'Voor zaken met een verzorgingsgebied: combinatie van GBP, lokale citations, branche-listings en geo-targeted ads. Domineer uw stad.',
        wat: [
          'Citation-consistentie (NAW op 50+ directories)',
          'Lokale landingspages per stad/regio',
          'Geo-targeted Google & Meta Ads',
          'Buurt-content (markteten, evenementen)',
        ],
        uitvoering: 'AI publiceert lokale content + monitort NAW-inconsistenties. Wij claimen ontbrekende listings.',
        tiers: ['Self-Service €14,95: audit', 'Managed AI: volledige uitvoering'],
      },
    ],
  },
  {
    id: 'advertising',
    title: 'Slimmere Advertenties',
    intro: 'Niet meer geld in een blackbox stoppen — AI optimaliseert dagelijks op conversies.',
    services: [
      {
        id: 'sea',
        slug: 'ai-google-ads',
        icon: Target,
        title: 'AI Google Ads (SEA)',
        seoIntent: 'advertenties slimmer laten draaien',
        intro: 'Google Ads-campagnes die dagelijks worden geoptimaliseerd op de zoekintenties die echt converteren — niet alleen op klikken.',
        wat: [
          'Kwaliteit-score-monitoring per ad',
          'Negatieve zoekwoorden automatisch toevoegen',
          'Bid-strategie per zoekterm op basis van ROAS',
          'Conversie-attributie multi-channel',
          'Wekelijkse rapportage met aanbevelingen',
        ],
        uitvoering: 'Direct verbonden via Google Ads API. AI past dagelijks bids en negatives aan. U keurt strategische wijzigingen goed (campagne-launch, budget-verhoging).',
        tiers: ['Self-Service €14,95: campagne-templates', 'Managed AI: volledig beheer'],
      },
      {
        id: 'social-ads',
        slug: 'meta-tiktok-snapchat-ads',
        icon: Globe,
        title: 'Meta · TikTok · Snapchat Ads',
        seoIntent: 'social media advertenties beheren',
        intro: 'Bereik uw klanten waar ze tijd doorbrengen. AI test creatieven en target zonder verspilling.',
        wat: [
          'Look-a-like audiences op basis van klantdata',
          'UGC-creatief templates (script + structuur)',
          'A/B-testing per platform',
          'Cross-channel ROAS-tracking',
          'Pixel-implementatie + conversie-API',
        ],
        uitvoering: 'Pixel + conversie-API via cms_executor of via uw bestaande GTM-setup. AI beheert biedingen, ons team levert de creatieve concepten.',
        tiers: ['Self-Service €14,95: creatieve templates', 'Managed AI: volledig beheer'],
      },
    ],
  },
  {
    id: 'website',
    title: 'Website Verbeteren of Nieuw Maken',
    intro: 'Uw website is uw belangrijkste verkoper. Wij houden hem fit — of bouwen een betere.',
    services: [
      {
        id: 'website-optimalisatie',
        slug: 'website-optimalisatie',
        icon: Wrench,
        title: 'Website Optimalisatie',
        seoIntent: 'website automatisch verbeteren',
        intro: 'Continue mutaties op uw bestaande site: speed, UX, conversie-elementen. Geen redesign nodig — alleen slimme verbeteringen.',
        wat: [
          'Core Web Vitals (LCP, INP, CLS)',
          'Above-fold optimalisatie',
          'CTA + formulier-tweaks',
          'Mobile-first responsiveness',
          'Image-compressie + lazy loading',
        ],
        uitvoering: 'Auto-push voor WP/Shopify via REST API. Snippets voor Wix/Squarespace. Ops-team voor Joomla/Drupal (SLA 5 werkdagen).',
        tiers: ['Self-Service €14,95: tip-lijst', 'Managed AI: AI voert fixes uit'],
      },
      {
        id: 'redesign',
        slug: 'website-redesign',
        icon: Crown,
        title: 'Website Redesign',
        seoIntent: 'website laten vernieuwen',
        intro: 'Strategisch redesign van uw bestaande site — conversie-eerst, niet design-first. Inbegrepen in Managed AI.',
        wat: [
          'User-flow audit (waar haakt bezoeker af)',
          'Hero + structuur rewrite',
          'CMS-migratie indien nodig',
          'A/B-test launch (oude vs. nieuwe)',
          'Live migratie zonder SEO-verlies',
        ],
        uitvoering: 'Strategie + design door ons team, implementatie door AI bot via CMS-API. Live binnen 4-6 weken.',
        tiers: ['Managed AI: inbegrepen'],
      },
      {
        id: 'nieuwe-website',
        slug: 'nieuwe-website-laten-maken',
        icon: Sparkles,
        title: 'Nieuwe Website Laten Maken',
        seoIntent: 'nieuwe website laten maken',
        intro: 'Voor ondernemers zonder website of met een verouderde site: AI-gegenereerde basis + handmatige finetune door ons team.',
        wat: [
          'AI content + structuur (op basis van uw branche)',
          'CMS keuze (WordPress of Shopify)',
          'Hosting + domeinen geregeld',
          'Live binnen 14 dagen',
          'SEO-optimaal vanaf launch',
        ],
        uitvoering: 'AI levert eerste versie (content + structuur), ons design-team finetuned, u keurt goed, wij publiceren.',
        tiers: ['Managed AI: inbegrepen'],
      },
    ],
  },
  {
    id: 'groei',
    title: 'Autoriteit & Groei',
    intro: 'Backlinks, content en data-driven optimalisatie — voor consistente lange-termijn groei.',
    services: [
      {
        id: 'backlinks',
        slug: 'backlinks-laten-bouwen',
        icon: Link2,
        title: 'Backlinks & Online Vermeldingen',
        seoIntent: 'autoriteit en backlinks bouwen',
        intro: '80+ NL directories in onze catalog. AI selecteert branche-specifieke en hoog-DA listings — geen massa-spam.',
        wat: [
          '35 algemene NL/EU directories (KvK, Telefoonboek, Yelp NL, Europages, Bing Places...)',
          '46+ branche-specifieke (Werkspot voor bouw, Iens voor horeca, ZorgkaartNL voor zorg)',
          'Reviewplatforms (Trustpilot, Kiyoh, Klantenvertellen)',
          'Outreach naar relevante NL blogs',
          'Maandelijks 25-40 nieuwe backlinks',
        ],
        uitvoering: 'AI doet de outreach via email_outbox (paused-aware). Wij claimen profielen, beheren NAW-consistentie en monitoren rejections.',
        tiers: ['Free Scan: top-3 kansen', 'Self-Service €14,95: outreach-templates', 'Managed AI: 25-40/maand'],
      },
      {
        id: 'content',
        slug: 'content-strategie',
        icon: FileText,
        title: 'Content & Strategie',
        seoIntent: 'meer organisch verkeer',
        intro: 'AI schrijft, ons team redigeert. Maandelijks 4-8 SEO-geoptimaliseerde artikelen die uw expertise bewijzen en organisch verkeer aantrekken.',
        wat: [
          'Keyword research op zoekintenties',
          'Topic clusters (hoofd-pagina + sub-artikelen)',
          'Blog + landingspages',
          'AI-publicatie direct naar uw CMS',
          'Internal linking optimalisatie',
        ],
        uitvoering: 'Claude Sonnet via emergentintegrations schrijft, ops-redactie checkt, AI publiceert via cms_executor.',
        tiers: ['Self-Service €14,95: AI-suggesties', 'Managed AI: 4-8 artikelen/maand'],
      },
      {
        id: 'analytics',
        slug: 'google-analytics-dashboard',
        icon: BarChart3,
        title: 'Google Analytics & Dashboard',
        seoIntent: 'meer leads uit mijn website',
        intro: 'GA4 + GSC + conversie-events. Custom dashboard waarin u per dienst en kanaal exact ziet wat omzet oplevert.',
        wat: [
          'GA4 via Google Tag Manager',
          'Conversie-doelen voor formulieren en telefoonclicks',
          'Search Console integratie',
          'Custom rapporten per kanaal',
          'Wekelijkse e-mail highlights',
        ],
        uitvoering: 'Setup eenmalig via guided flow of automatisch via WordPress/Shopify integratie. Dashboard live op /platform.',
        tiers: ['Self-Service €14,95: dashboard-toegang', 'Managed AI: + custom rapporten'],
      },
      {
        id: 'cro',
        slug: 'conversie-optimalisatie',
        icon: TrendingUp,
        title: 'Conversie-Optimalisatie (CRO)',
        seoIntent: 'meer klanten uit mijn website',
        intro: 'AI-analyse van uitvallers, hitmap-data en formulier-completion. Iteratieve verbeteringen per maand.',
        wat: [
          'CRO-audit per pagina (vooruitgang per quarter)',
          'Heatmap + scroll-depth tracking',
          'Formulier-optimalisatie (veld voor veld)',
          'CTA-positionering en copy-tests',
          'A/B-tests op hero + checkout',
        ],
        uitvoering: 'Microsoft Clarity-integratie + AI-analyse. Mutaties via cms_executor of snippet-mode.',
        tiers: ['Self-Service €14,95: audit', 'Managed AI: maandelijkse iteraties'],
      },
    ],
  },
];

/* ─── Schema.org structured data ─── */
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.imperiaai.nl/" },
      { "@type": "ListItem", "position": 2, "name": "Diensten", "item": "https://www.imperiaai.nl/diensten" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "AI Marketing Platform",
    "provider": { "@type": "Organization", "name": "IMPERIA AI", "url": "https://www.imperiaai.nl" },
    "areaServed": { "@type": "Country", "name": "Nederland" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Diensten",
      "itemListElement": CATEGORIES.flatMap((c) =>
        c.services.map((s) => ({
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": s.title, "description": s.intro },
        })),
      ),
    },
  },
];

export default function DienstenPage() {
  return (
    <>
      <SEOHead
        title="AI Marketing Diensten voor MKB · SEO, Google Ads, Backlinks & Website laten maken"
        description="12 AI-gestuurde marketing diensten voor MKB & zzp: online vindbaarheid verbeteren, Google Bedrijfsprofiel optimalisatie, lokale SEO Nederland, website laten maken voor zzp/mkb, backlinks bouwen en advertenties beheer."
        canonical="/diensten"
        keywords="ai marketing manager mkb, online vindbaarheid verbeteren, google bedrijfsprofiel optimalisatie, website laten maken zzp, website laten maken mkb, lokale seo nederland, ai seo, backlinks bouwen mkb, google ads beheer mkb, conversie optimalisatie"
        schema={SCHEMA}
      />

      {/* ═══ HERO ═══ */}
      <section style={{
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
        padding: '90px 0 70px', color: 'white',
      }}>
        <div className="container-custom max-w-5xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 bg-[#D4A017]/15 border border-[#D4A017]/30 rounded-full px-3 py-1.5 mb-6">
            <Bot size={12} className="text-[#D4A017]" />
            <span className="text-[#D4A017] text-xs font-bold tracking-wide uppercase">12 diensten · 1 AI-platform</span>
          </span>
          <h1
            data-testid="diensten-hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6"
            style={{ fontFamily: "'Sora', sans-serif" }}>
            AI-gestuurde marketing diensten — uitgevoerd, niet alleen geadviseerd
          </h1>
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-8">
            Van SEO tot Google Ads, van backlinks tot conversie-optimalisatie. Onze AI bot voert het standaardwerk daadwerkelijk uit en ons team handelt de uitzonderingen af. U ziet realtime wat er gebeurt — geen vage rapportages.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/register" data-testid="diensten-cta-scan" style={{
              background: '#D4A017', color: '#0f172a', padding: '13px 26px',
              borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Start gratis scan <ArrowRight size={14} />
            </Link>
            <Link to="/prijzen" style={{
              border: '1px solid rgba(255,255,255,0.3)', color: 'white',
              padding: '13px 26px', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Bekijk prijzen
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ EXECUTION MODES STRIP — eerlijke uitleg van uitvoer-modi ═══ */}
      <section style={{ background: 'white', padding: '60px 0 50px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Hoe AI uw site daadwerkelijk aanpast
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              3 uitvoer-modi — eerlijk per CMS
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Bot, title: 'Auto-push', color: '#15803d', bg: '#dcfce7', border: '#86efac',
                cms: 'WordPress · Shopify · Webflow · Magento · PrestaShop · BigCommerce',
                desc: 'AI pusht mutaties direct via REST API. U koppelt eenmalig met Application Password of OAuth — daarna voert AI fixes uit binnen 15 minuten.',
              },
              {
                icon: UserCheck, title: 'Snippet-modus', color: '#0369a1', bg: '#e0f2fe', border: '#7dd3fc',
                cms: 'Wix · Squarespace · Webador · JouwWeb · Strikingly · Site123',
                desc: 'Geen schrijf-API beschikbaar? AI genereert kant-en-klare blokken (HTML/snippet) met provider-specifieke plaats-instructies. U plakt in 30 seconden.',
              },
              {
                icon: Headphones, title: 'Team-modus', color: '#0e7490', bg: '#cffafe', border: '#67e8f9',
                cms: 'Joomla · Drupal · TYPO3 · Mijndomein · Hostnet · Combell · Versio',
                desc: 'Ons ops-specialist plaatst handmatig in uw CMS — binnen 5 werkdagen SLA. AI levert de exacte content en plaatsings-instructies aan ons team.',
              },
            ].map((m) => (
              <div key={m.title} style={{
                background: m.bg, borderRadius: 14, padding: '20px 18px',
                border: `1px solid ${m.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <m.icon size={18} style={{ color: m.color }} />
                  </div>
                  <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: m.color }}>{m.title}</h3>
                </div>
                <p style={{ fontSize: 11, color: m.color, fontWeight: 600, marginBottom: 8 }}>{m.cms}</p>
                <p style={{ fontSize: 13, color: '#0f172a', lineHeight: 1.55 }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CATEGORIES + SERVICES ═══ */}
      {CATEGORIES.map((cat) => (
        <section key={cat.id} id={cat.id} style={{
          background: cat.id === 'advertising' || cat.id === 'groei' ? '#f8fafc' : 'white',
          padding: '70px 0 60px',
        }}>
          <div className="container-custom max-w-6xl mx-auto px-4">
            <div className="mb-10 max-w-3xl">
              <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 6 }}>
                Categorie · {cat.services.length} diensten
              </p>
              <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 8 }}>
                {cat.title}
              </h2>
              <p style={{ fontSize: 15, color: '#64748b', lineHeight: 1.55 }}>{cat.intro}</p>
            </div>

            <div className="grid gap-6">
              {cat.services.map((s) => (
                <article key={s.id} id={s.id} data-testid={`service-${s.id}`} style={{
                  background: 'white', borderRadius: 16, padding: '28px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                }}>
                  <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left: title + intro */}
                    <div className="lg:col-span-1">
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #D4A017, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                        <s.icon size={22} style={{ color: '#0f172a' }} />
                      </div>
                      <p style={{ fontSize: 10, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 4 }}>
                        Zoekintentie: {s.seoIntent}
                      </p>
                      <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 8 }}>
                        {s.title}
                      </h3>
                      <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 12 }}>{s.intro}</p>
                      {s.slug && (
                        <Link
                          to={`/${s.slug}`}
                          data-testid={`service-readmore-${s.id}`}
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0f172a] hover:text-[#D4A017] transition-colors"
                        >
                          Volledige uitleg <ChevronRight size={14} />
                        </Link>
                      )}
                    </div>

                    {/* Middle: wat krijgt klant */}
                    <div>
                      <h4 style={{ fontSize: 11, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 10 }}>
                        Wat u krijgt
                      </h4>
                      <ul style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {s.wat.map((w, i) => (
                          <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                            <CheckCircle2 size={14} style={{ color: '#15803d', flexShrink: 0, marginTop: 2 }} />
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Right: uitvoering + tiers */}
                    <div>
                      <h4 style={{ fontSize: 11, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>
                        Hoe wij dit uitvoeren
                      </h4>
                      <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.55, marginBottom: 14, background: '#f8fafc', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        {s.uitvoering}
                      </p>
                      <h4 style={{ fontSize: 11, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>
                        Inbegrepen in
                      </h4>
                      <ul style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {s.tiers.map((t, i) => (
                          <li key={i} style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#D4A017' }} />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ═══ MID CTA — naar prijzen ═══ */}
      <section style={{ background: '#0f172a', padding: '60px 0', color: 'white' }}>
        <div className="container-custom max-w-4xl mx-auto px-4 text-center">
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, lineHeight: 1.15, marginBottom: 8 }}>
            Welke tier past bij u?
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', marginBottom: 20, maxWidth: 540, margin: '0 auto 20px' }}>
            Gratis Scan (€0), Connected Self-Service (€14,95 p/m excl. btw) of Managed AI Bot (€995,95 excl. btw eerste maand, daarna €2.495,95 p/m excl. btw).
          </p>
          <Link to="/prijzen" style={{
            background: '#D4A017', color: '#0f172a', padding: '13px 26px',
            borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            Bekijk volledige prijsstructuur <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ═══ INTEGRATIES STRIP ═══ */}
      <section id="integraties" style={{ background: 'white', padding: '60px 0' }}>
        <div className="container-custom max-w-5xl mx-auto px-4 text-center">
          <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
            Integraties
          </p>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 10 }}>
            Werkt met de tools die u al gebruikt
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', maxWidth: 580, margin: '0 auto 24px' }}>
            Koppel WordPress, Shopify, Google Ads, Meta Ads, GA4, Search Console, Bedrijfsprofiel of uw eigen CMS. Alleen lezen — wij schrijven pas na uw goedkeuring.
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {['WordPress', 'Shopify', 'Webflow', 'Magento', 'Wix', 'Squarespace', 'Webador', 'JouwWeb', 'Google Ads', 'Meta Ads', 'GA4', 'Search Console', 'Google Business', 'Stripe', 'Trustpilot', 'Kiyoh'].map((i) => (
              <span key={i} style={{
                background: '#f8fafc', border: '1px solid #e2e8f0',
                padding: '6px 14px', borderRadius: 999,
                fontSize: 12, fontWeight: 600, color: '#475569',
              }}>{i}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section style={{ background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', padding: '80px 0', color: 'white' }}>
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, lineHeight: 1.15, marginBottom: 10 }}>
            Klaar om uw <span style={{ color: '#D4A017' }}>marketing</span> te laten uitvoeren?
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', marginBottom: 28, maxWidth: 520, margin: '0 auto 28px', lineHeight: 1.55 }}>
            Start met een gratis online groeiscan. Wij scannen uw site + concurrenten + Google + backlink-kansen in 60 seconden.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" style={{
              background: '#D4A017', color: '#0f172a', padding: '14px 28px',
              borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Start gratis scan <ArrowRight size={14} />
            </Link>
            <Link to="/contact" style={{
              border: '1px solid rgba(255,255,255,0.3)', color: 'white',
              padding: '14px 28px', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Persoonlijk advies <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
